'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import type { DSLFile, FormDSL, FormDSLContext } from '@commenda/types';
import { FormsTemplateType } from '@commenda/types';
import { useContext, useMemo } from 'react';
import { useComponentRegistry } from '../../../hooks/ComponentRegistryContext';
import { FormContext, type FormContextType } from '../../../hooks/FormContext';
import { buildComponentMap } from '../../../utils/dsl-traversal';
import { LinkedFormRenderer } from '../LinkedFormRenderer';

export interface OwnerSubFormProps {
  /** FormTemplateKey configured on the node; without one there is nothing to render. */
  keyId?: string;
  /** Answers to re-open with. The caller re-keys per target. */
  defaultValues?: Record<string, unknown>;
  /**
   * Drops `directorId` from the inherited context so a person sub-form creates
   * rather than updating whoever the host form happens to be scoped to.
   */
  omitDirectorId?: boolean;
  /** Scopes the sub-form to this saved person, so it prefills and updates their record. */
  directorId?: number;
  onSubmit: (data: Record<string, unknown>) => void;
  onClose?: () => void;
  /**
   * Renders the template as a section of the host's panel: no page header and
   * no buttons of its own. Pair with `submitRef` to drive the submit.
   */
  inline?: boolean;
  registerSubmit?: (submit: (() => void) | null) => void;
  /** Shown when the node has no linked template — a builder misconfiguration. */
  missingTemplateHint: string;
}

const EMPTY: Record<string, unknown> = {};

const Message = ({
  isError,
  title,
  body,
  onClose,
}: {
  isError: boolean;
  title: string;
  body: string;
  onClose?: () => void;
}) => (
  <div className="flex h-full flex-col items-center justify-center py-12 text-center">
    <Icons.AlertCircle
      className={`mx-auto mb-4 size-12 ${isError ? 'text-text-error' : 'text-text-warning'}`}
    />
    <p className="txt-medium-medium mb-2 text-text-primary">{title}</p>
    <p className="txt-small-regular text-text-tertiary">{body}</p>
    {onClose ? (
      <Button variant="secondary" size="sm" className="mt-4" onClick={onClose}>
        Close
      </Button>
    ) : null}
  </div>
);

/**
 * Renders a linked form template for one cap-table owner, either inline in the
 * add/edit sheet or on its own in a drawer.
 *
 * The sub-form is its own FormContext, so a field sourcing options from a
 * sibling (`dataSource.formField`) would see an empty form. The host's value
 * for each such source is seeded in, which is how an entity's authorized
 * representative can be picked from the people added earlier in the form.
 */
export const OwnerSubForm = ({
  keyId,
  defaultValues = EMPTY,
  omitDirectorId,
  directorId,
  onSubmit,
  onClose,
  inline,
  registerSubmit,
  missingTemplateHint,
}: OwnerSubFormProps) => {
  const parentContext = useContext(FormContext);
  const componentRegistry = useComponentRegistry();

  const { key: formKey } = useFormTemplateKey(keyId ?? null);
  const { template, isLoading } = useFormsTemplate(formKey?.liveFormId ?? null);

  const siblingSourceIds = useMemo(() => {
    if (!template?.dsl) return [] as string[];
    const components = buildComponentMap(template.dsl as unknown as DSLFile);
    const ids = new Set<string>();
    Object.values(components).forEach((component) => {
      const moduleId = (
        component as { dataSource?: { formField?: { moduleId?: string } } }
      ).dataSource?.formField?.moduleId;
      if (moduleId) ids.add(moduleId);
    });
    return [...ids];
  }, [template?.dsl]);

  const seededValues = useMemo(() => {
    const seeded: Record<string, unknown> = { ...defaultValues };
    siblingSourceIds.forEach((id) => {
      if (seeded[id] === undefined) seeded[id] = parentContext.formData[id];
    });
    return seeded;
  }, [defaultValues, siblingSourceIds, parentContext.formData]);

  const subFormContext = useMemo((): FormContextType => {
    const inherited = parentContext.dslContext;
    if (!inherited || (directorId == null && !omitDirectorId)) {
      return parentContext;
    }
    const scoped: FormDSLContext = { ...inherited };
    if (directorId != null) scoped.directorId = directorId;
    else delete scoped.directorId;
    return { ...parentContext, dslContext: scoped };
  }, [parentContext, omitDirectorId, directorId]);

  const handleSubmit = (data: Record<string, unknown>) => {
    // The seeded sibling values are host state on loan — storing them back would
    // freeze a stale copy of the people list onto the row.
    const kept = { ...data };
    siblingSourceIds.forEach((id) => delete kept[id]);
    onSubmit(kept);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <LoadingIndicator />
      </div>
    );
  }

  if (!keyId || !template) {
    return (
      <Message
        isError={!!keyId}
        title={keyId ? 'Form template not found' : 'No form linked'}
        body={missingTemplateHint}
        onClose={onClose}
      />
    );
  }

  return (
    <FormContext.Provider value={subFormContext}>
      <LinkedFormRenderer
        type={template.type as FormsTemplateType}
        dsl={template.dsl as FormDSL}
        onClose={onClose}
        onSubmit={handleSubmit}
        defaultValues={seededValues}
        layoutSettings={{
          hideTopHeader: true,
          hideHeader: inline,
          hideActions: inline,
        }}
        embedMode
        registerSubmit={registerSubmit}
        componentRegistry={componentRegistry}
      />
    </FormContext.Provider>
  );
};
